import { Link } from '@packages/modules/src/Link/Link';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinLinkClick } from '@packages/tracking/src/types/events';
import { type Dispatch, type HTMLAttributes, type ReactNode, type SetStateAction } from 'react';
import { useIntl } from 'react-intl';
import type { MiniDetailViewDataQuery } from '@packages/gql/generated/shopping/graphql';
import { AvailabilityInfo } from '../../AvailabilityInfo/AvailabilityInfo.client';
import { EfficiencyFlagsAndDataSheetsDownloadCard } from '../../EfficiencyFlagsAndDataSheetsDownloadCard/EfficiencyFlagsAndDataSheetsDownloadCard';
import { PriceDV } from '../../PriceDV/PriceDV.client';
import { ProductPowerSupply } from '../../ProductPowerSupply/ProductPowerSupply.client';
import { QuantitySelectionAtomized } from '../../QuantitySelectionAtomized/QuantitySelectionAtomized.client';
import { miniDVMessages } from '../messages';
import { VariationSelection } from '../../VariationSelection/VariationSelection';

export type MiniDVProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * MiniDV product data
   */
  data: NonNullable<MiniDetailViewDataQuery['product']>;
  /**
   * setter for selectedVariation
   */
  setSelectedVariation: Dispatch<SetStateAction<string | null>>;
  /**
   * quantity of the lineItem
   */
  quantity: number;
  /**
   * Servicelayer component for AvailabilityInfo
   */
  serviceLayer: ReactNode;
  hideCounter?: boolean;
};

export const MiniDV = ({
  data,
  setSelectedVariation,
  quantity,
  serviceLayer,
  hideCounter = false,
  ...props
}: MiniDVProps) => {
  const dispatchGlycerin = useTracking();
  const { language, localizeUrl } = useI18n();
  const { formatMessage } = useIntl();

  const {
    name,
    media,
    price,
    tireEfficiency,
    powerEfficiencyFlags,
    sku: productSku,
    dimensions,
    powerSupply,
    availability,
  } = data;

  return (
    <div
      className="flex flex-col gap-1.5 px-1.5 py-2.5 md:flex-row md:flex-nowrap md:gap-3 md:px-2.5"
      {...props}
    >
      <div className="w-full md:w-1/2">
        <div className="flex flex-row gap-1.5 md:flex-col md:gap-1">
          <div className="basis-1/3 md:basis-full">
            <Image
              src={media[0].url}
              alt={name}
              width={530}
              height={530}
              className="object-contain"
              unoptimized
            />
          </div>
          <div className="basis-2/3 md:basis-auto">
            <div className="flex flex-col gap-0.5 md:gap-1">
              <div className="text-size-body-sm md:text-size-body-base font-bold">{name}</div>
              {/* AvailabilityInfo */}
              <AvailabilityInfo
                availabilityMessage={availability.message}
                availabilityState={availability.state}
                serviceLayer={serviceLayer}
              />
              {/* Price */}
              <PriceDV data={price} />
              {/* Energy or Tire-Efficiency */}
              {((tireEfficiency && tireEfficiency.flags.length > 0) ||
                (powerEfficiencyFlags && powerEfficiencyFlags.length > 0)) && (
                <EfficiencyFlagsAndDataSheetsDownloadCard data={data} />
              )}
              {/* QuantitySelection */}
              {!hideCounter && (
                <div className="mt-1">
                  <QuantitySelectionAtomized quantitySelectionProps={{ quantity }} hideLabel />
                </div>
              )}
              {/* Power Supply // FuAG */}
              {powerSupply && <ProductPowerSupply maskedData={powerSupply} />}
            </div>
          </div>
        </div>
        {/* call to action for the DetailView link */}
        <div className="mt-2 md:mt-1.5">
          <Link
            href={localizeUrl(`/p/${productSku}`, language)}
            tabIndex={-1}
            onClick={() =>
              dispatchGlycerin<GTMEventGlycerinLinkClick>({
                LinkClickData: {
                  detail: formatMessage(miniDVMessages.artikelButton),
                  label: 'Navi-Click',
                  category: 'Mini-DV',
                },
                event: 'LinkClick',
              })
            }
          >
            <Button className="w-full">{formatMessage(miniDVMessages.artikelButton)}</Button>
          </Link>
        </div>
      </div>
      <Divider flexItem className="-mx-1.5 md:border-r-[thin]" />
      <div className="w-full md:w-1/2">
        <VariationSelection
          data={dimensions}
          sampleOrderLink={null}
          onSelect={setSelectedVariation}
        />
      </div>
    </div>
  );
};
